import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import profileService, { UserStats, ActivityItem, UserSettings } from '../../services/profileService';
import { addNotification } from './uiSlice';

interface ProfileState {
  stats: UserStats | null;
  activities: ActivityItem[];
  settings: UserSettings | null;
  loading: {
    stats: boolean;
    activities: boolean;
    settings: boolean;
    avatar: boolean;
  };
  error: string | null;
  message: string | null;
}

const initialState: ProfileState = {
  stats: null,
  activities: [],
  settings: null,
  loading: {
    stats: false,
    activities: false,
    settings: false,
    avatar: false,
  },
  error: null,
  message: null,
};

// Async thunks
export const fetchUserStats = createAsyncThunk(
  'profile/fetchUserStats',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const stats = await profileService.getUserStats();
      return stats;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch user statistics';
      dispatch(addNotification({ 
        type: 'error', 
        message: errorMessage,
        duration: 5000 
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchRecentActivity = createAsyncThunk(
  'profile/fetchRecentActivity',
  async (limit: number = 10, { rejectWithValue, dispatch }) => {
    try {
      const activities = await profileService.getRecentActivity(limit);
      return activities;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch recent activity';
      dispatch(addNotification({ 
        type: 'error', 
        message: errorMessage,
        duration: 5000 
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchUserSettings = createAsyncThunk(
  'profile/fetchUserSettings',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const settings = await profileService.getSettings();
      return settings;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch user settings';
      dispatch(addNotification({ 
        type: 'error', 
        message: errorMessage,
        duration: 5000 
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUserSettings = createAsyncThunk(
  'profile/updateUserSettings',
  async (settings: Partial<UserSettings>, { rejectWithValue, dispatch }) => {
    try {
      const updatedSettings = await profileService.updateSettings(settings);
      dispatch(addNotification({ 
        type: 'success', 
        message: 'Settings updated successfully!',
        duration: 5000 
      }));
      return updatedSettings;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update settings';
      dispatch(addNotification({ 
        type: 'error', 
        message: errorMessage,
        duration: 5000 
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const uploadAvatar = createAsyncThunk(
  'profile/uploadAvatar',
  async (file: File, { rejectWithValue, dispatch }) => {
    try {
      const result = await profileService.uploadAvatar(file);
      dispatch(addNotification({ 
        type: 'success', 
        message: 'Avatar updated successfully!',
        duration: 5000 
      }));
      return result;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to upload avatar';
      dispatch(addNotification({ 
        type: 'error', 
        message: errorMessage,
        duration: 5000 
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteAvatar = createAsyncThunk(
  'profile/deleteAvatar',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      await profileService.deleteAvatar();
      dispatch(addNotification({ 
        type: 'success', 
        message: 'Avatar removed successfully!',
        duration: 5000 
      }));
      return null;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete avatar';
      dispatch(addNotification({ 
        type: 'error', 
        message: errorMessage,
        duration: 5000 
      }));
      return rejectWithValue(errorMessage);
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    addActivity: (state, action: PayloadAction<ActivityItem>) => {
      state.activities.unshift(action.payload);
      // Keep only the latest 50 activities
      if (state.activities.length > 50) {
        state.activities = state.activities.slice(0, 50);
      }
    },
    updateStats: (state, action: PayloadAction<Partial<UserStats>>) => {
      if (state.stats) {
        state.stats = { ...state.stats, ...action.payload };
      }
    },
    updateSetting: (state, action: PayloadAction<{ key: keyof UserSettings; value: unknown }>) => {
      if (state.settings) {
        (state.settings as any)[action.payload.key] = action.payload.value;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch User Stats
      .addCase(fetchUserStats.pending, (state) => {
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchUserStats.fulfilled, (state, action: PayloadAction<UserStats>) => {
        state.loading.stats = false;
        state.stats = action.payload;
      })
      .addCase(fetchUserStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload as string;
      })
      
      // Fetch Recent Activity
      .addCase(fetchRecentActivity.pending, (state) => {
        state.loading.activities = true;
        state.error = null;
      })
      .addCase(fetchRecentActivity.fulfilled, (state, action: PayloadAction<ActivityItem[]>) => {
        state.loading.activities = false;
        state.activities = action.payload;
      })
      .addCase(fetchRecentActivity.rejected, (state, action) => {
        state.loading.activities = false;
        state.error = action.payload as string;
      })
      
      // Fetch User Settings
      .addCase(fetchUserSettings.pending, (state) => {
        state.loading.settings = true;
        state.error = null;
      })
      .addCase(fetchUserSettings.fulfilled, (state, action: PayloadAction<UserSettings>) => {
        state.loading.settings = false;
        state.settings = action.payload;
      })
      .addCase(fetchUserSettings.rejected, (state, action) => {
        state.loading.settings = false;
        state.error = action.payload as string;
      })
      
      // Update User Settings
      .addCase(updateUserSettings.pending, (state) => {
        state.loading.settings = true;
        state.error = null;
      })
      .addCase(updateUserSettings.fulfilled, (state, action: PayloadAction<UserSettings>) => {
        state.loading.settings = false;
        state.settings = action.payload;
        state.message = 'Settings updated successfully!';
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.loading.settings = false;
        state.error = action.payload as string;
      })
      
      // Upload Avatar
      .addCase(uploadAvatar.pending, (state) => {
        state.loading.avatar = true;
        state.error = null;
      })
      .addCase(uploadAvatar.fulfilled, (state) => {
        state.loading.avatar = false;
        state.message = 'Avatar updated successfully!';
      })
      .addCase(uploadAvatar.rejected, (state, action) => {
        state.loading.avatar = false;
        state.error = action.payload as string;
      })
      
      // Delete Avatar
      .addCase(deleteAvatar.pending, (state) => {
        state.loading.avatar = true;
        state.error = null;
      })
      .addCase(deleteAvatar.fulfilled, (state) => {
        state.loading.avatar = false;
        state.message = 'Avatar removed successfully!';
      })
      .addCase(deleteAvatar.rejected, (state, action) => {
        state.loading.avatar = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearMessage, addActivity, updateStats, updateSetting } = profileSlice.actions;

export default profileSlice.reducer; 